import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { CREATE_NEW_TEMPLATE_VALUE } from "constants/incorporation";
import { useToast } from "hooks/useToast";
import {
  useAssignIncorporationTemplateMutation,
  useCreateIncorporationTemplateMutation,
} from "store/apis/incorporation";
import { useGetFormTemplateListQuery } from "store/apis/incorporationFormBuilder";
import { BackendError } from "types/utils/error";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";

const MAX_TEMPLATE_OPTIONS = 500;

type FormValues = {
  formTemplateId: string;
};

type AssignTemplateModalProps = {
  isOpen: boolean;
  close: () => void;
  groupId: string;
};

export const AssignTemplateModal = ({
  isOpen,
  close,
  groupId,
}: AssignTemplateModalProps) => {
  const history = useHistory();
  const { alertToast } = useToast();
  const { data } = useGetFormTemplateListQuery({
    search: null,
    page_size: MAX_TEMPLATE_OPTIONS,
  });

  const [assignTemplate, { isLoading: isAssigning }] =
    useAssignIncorporationTemplateMutation();
  const [createTemplate, { isLoading: isCreating }] =
    useCreateIncorporationTemplateMutation();

  const existingTemplateOptions = useMemo(
    () =>
      (data?.results || []).map((template) => ({
        label: template.title,
        value: template.uuid,
      })),
    [data]
  );

  const createNewTemplateOption = {
    label: "Create a new template",
    value: CREATE_NEW_TEMPLATE_VALUE,
  };

  const allTemplateOptions = [
    ...existingTemplateOptions,
    createNewTemplateOption,
  ];

  const openFormEditor = (formInstanceId: string) => {
    close();
    history.push(`/admin/incorporation/tracker/${formInstanceId}?edit=true`);
  };

  const onSubmit = async (values: FormValues) => {
    try {
      if (values.formTemplateId === CREATE_NEW_TEMPLATE_VALUE) {
        const { form_instance_id } = await createTemplate({
          groupId,
        }).unwrap();
        openFormEditor(form_instance_id);
        return;
      }

      const { form_instance_id } = await assignTemplate({
        groupId,
        formTemplateId: values.formTemplateId,
      }).unwrap();
      openFormEditor(form_instance_id);
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  const isSubmitting = isAssigning || isCreating;

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content useCustomOverlay asChild>
        <FormikForm<FormValues>
          initialValues={{ formTemplateId: "" }}
          onSubmit={onSubmit}
        >
          {({ values, setFieldValue }) => (
            <>
              <Modal.Header>
                <Modal.Title>Assign Incorporation Template</Modal.Title>
                <Modal.Close />
              </Modal.Header>

              <Modal.Body>
                <Combobox
                  withForm
                  name="formTemplateId"
                  label="Select Template"
                  required
                  options={existingTemplateOptions}
                  value={allTemplateOptions.find(
                    (option) => option.value === values.formTemplateId
                  )}
                  placeholder="Select template"
                  menuPortalTarget={document.body}
                  isClearable
                  actions={
                    <Button
                      type="button"
                      customType="ghost"
                      block
                      className="!t-justify-start !t-px-0 !t-font-medium !t-text-text-100"
                      onClick={() => {
                        setFieldValue(
                          "formTemplateId",
                          CREATE_NEW_TEMPLATE_VALUE
                        );
                        if (document.activeElement instanceof HTMLElement) {
                          document.activeElement.blur();
                        }
                      }}
                    >
                      Create a new template
                    </Button>
                  }
                />
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Go back</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !values.formTemplateId}
                >
                  Proceed
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
